/**
 * Loading customer logos for a CANVAS renderer.
 *
 * The flat map draws its markers as DOM, so an `<img>` tag does the work. The
 * 3D globe is one `<canvas>` repainted every frame, and `ctx.drawImage` needs
 * an `HTMLImageElement` that has already decoded — a fresh `new Image()` per
 * frame would fetch 184 logos sixty times a second and draw nothing.
 *
 * So this is a tiny per-URL cache with exactly three states, and the renderer
 * only ever asks for the one it can use:
 *
 *   pending  — decoding; draw the fallback this frame
 *   ready    — an `HTMLImageElement`, safe to `drawImage`
 *   failed   — 404, wrong bytes, or a blocked request; never retried, because
 *              a per-frame renderer would retry it sixty times a second
 *
 * `onReady` is what repaints: a globe that is sitting still (auto-rotate
 * paused, no drag) would otherwise never show a logo that finished decoding
 * after the last frame.
 */

type Entry =
  | { state: "pending" }
  | { state: "ready"; image: HTMLImageElement }
  | { state: "failed" }

const cache = new Map<string, Entry>()

/**
 * The decoded image for `url`, or null while it is still loading — or forever,
 * if it cannot load. Never throws and never blocks.
 */
export function logoImage(url: string | null | undefined, onReady?: () => void): HTMLImageElement | null {
  if (!url) return null

  const hit = cache.get(url)
  if (hit) return hit.state === "ready" ? hit.image : null

  cache.set(url, { state: "pending" })

  const image = new Image()
  // The logo is served from the dashboard's own origin in the browser, but the
  // desktop shell loads the SPA from `tauri://localhost` and resolves the URL
  // against the dashboard host — a cross-origin fetch. Without this the canvas
  // would be tainted the moment one is drawn into it.
  image.crossOrigin = "anonymous"
  image.decoding = "async"
  image.onload = () => {
    // A zero-size decode is a broken image that fired `onload` anyway.
    cache.set(url, image.naturalWidth > 0 ? { state: "ready", image } : { state: "failed" })
    onReady?.()
  }
  image.onerror = () => {
    cache.set(url, { state: "failed" })
  }
  image.src = url

  return null
}

/** Test seam. The cache is module-level so it survives remounts. */
export function resetLogoImageCache(): void {
  cache.clear()
}
